/**
 * The topic layer's authoring rules — D231 (`docs/design/actors.md` §4, §7).
 *
 * @remarks
 * A topic table is a keyed answer (content-schema `topics.ts`), and every
 * rule here is the reference walk or the D31/D47 dead-content law applied to
 * it:
 *
 * - Topic ids are unique across the pack — the spent ledger is keyed by id
 *   alone, so two topics under one id would spend each other.
 * - A key with an underscore is an id reference and must name a declared
 *   item, room or (D232) actor; words have no underscores, so a typo in an id key would
 *   otherwise be an unreachable word nobody can type.
 * - One key once per topic: two TOPICS may share a key (the conditional arm,
 *   then the fallback arm — the second is unreachable only if the first
 *   always answers, and that is what `when` and `once` are for), but one
 *   topic listing the same key twice is a slip.
 * - A plain key is written as a noun is, without an article: the parser
 *   drops the player's "the" before it looks for a head, so "the frame"
 *   could never match what anyone types.
 * - `topicFallbackNarratives` needs `topics` — a fallback with nothing to
 *   fall back from is dead content.
 * - A topic's `when`, its narratives' conditional readings, and its effects
 *   walk like an interaction's (producibility of the `when` is the D184
 *   walk's, which finds conditions structurally and needs no help here).
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, isTopicIdKey, type TopicDefinition } from '@orpheus/content-schema';

import { brokenReference, collectDuplicates } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, checkNarrativeLines, type DeclaredIdSets, declaredIdSetsOf } from './reference-walkers.js';

/** A key opening with an article — `the`, `a`, `an` — which the parser strips from a typed phrase before matching. */
const LEADING_ARTICLE_PATTERN = /^(?:the|an?)\s/u;

/** One table with the path its owner sits at. */
interface LocatedTopicTable {
	readonly ownerPath: string;
	readonly topicFallbackNarratives: undefined | readonly unknown[];
	readonly topics: undefined | readonly TopicDefinition[];
}

/**
 * Every topic table in the pack — items', then rooms', then (D232) actors' — with its owner's path.
 *
 * @param contentPack - The parsed pack
 * @returns The tables, in document order
 */
const topicTablesOf = (contentPack: ContentPackDefinition): readonly LocatedTopicTable[] => [
	...contentPack.items.map(
		(item, itemIndex): LocatedTopicTable => ({
			ownerPath: `items[${String(itemIndex)}]`,
			topicFallbackNarratives: item.topicFallbackNarratives,
			topics: item.topics,
		}),
	),
	...contentPack.rooms.map(
		(room, roomIndex): LocatedTopicTable => ({
			ownerPath: `rooms[${String(roomIndex)}]`,
			topicFallbackNarratives: room.topicFallbackNarratives,
			topics: room.topics,
		}),
	),
	// D232: an actor carries the same table, judged by the same rules.
	...contentPack.actors.map(
		(actor, actorIndex): LocatedTopicTable => ({
			ownerPath: `actors[${String(actorIndex)}]`,
			topicFallbackNarratives: actor.topicFallbackNarratives,
			topics: actor.topics,
		}),
	),
];

/**
 * Validate every topic table and fallback in the pack.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateTopicAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);
	const tables = topicTablesOf(contentPack);

	collectDuplicates(
		tables.flatMap((table) =>
			(table.topics ?? []).map((topic, topicIndex) => ({
				declarationPath: `${table.ownerPath}.topics[${String(topicIndex)}].topicId`,
				declaredId: topic.topicId,
			})),
		),
		'topic',
		failures,
	);

	for (const table of tables) {
		if (table.topicFallbackNarratives !== undefined && table.topics === undefined) {
			failures.push(
				brokenReference(
					`${table.ownerPath}.topicFallbackNarratives`,
					table.ownerPath,
					'a fallback for when no topic answers, on a thing that declares no `topics` — nothing could ever fall through to it',
				),
			);
		}

		(table.topics ?? []).forEach((topic, topicIndex) => {
			checkTopic(topic, `${table.ownerPath}.topics[${String(topicIndex)}]`, declared, failures);
		});
	}

	return failures;
};

/**
 * Walk one topic: its keys, its gate, its lines, its effects.
 *
 * @param topic - The topic
 * @param topicPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkTopic = (topic: TopicDefinition, topicPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	const seenKeys = new Set<string>();

	topic.keys.forEach((key, keyIndex) => {
		const keyPath = `${topicPath}.keys[${String(keyIndex)}]`;
		const folded = key.trim().toLowerCase();

		if (seenKeys.has(folded)) {
			failures.push(brokenReference(keyPath, key, 'this topic lists the same key twice'));
		}

		seenKeys.add(folded);

		// The parser drops the player's article before it looks for a head, so
		// a key spelled with one ("the frame") is a phrase nobody can type.
		if (!isTopicIdKey(key) && LEADING_ARTICLE_PATTERN.test(folded)) {
			failures.push(brokenReference(keyPath, key, 'a topic key is written as a noun is, without its article — the parser never sees the player\'s "the", so this key could never match'));
		}

		if (isTopicIdKey(key) && !declared.itemIds.has(key) && !declared.roomIds.has(key) && !declared.actorIds.has(key)) {
			failures.push(
				brokenReference(
					keyPath,
					key,
					'a topic key with an underscore is an id reference, and the pack declares no item, room or actor by this id — a word the player could type has no underscore in it',
				),
			);
		}
	});

	if (topic.when !== undefined) {
		checkCondition(topic.when, `${topicPath}.when`, declared, failures);
	}

	checkNarrativeLines(topic.narratives, `${topicPath}.narratives`, declared, failures);
};
